import {Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output} from '@angular/core';

export interface ImageUploadLimitExceededEvent {
  remainingSlots: number;
  selectedCount: number;
}

@Directive({
  selector: '[imageUploadLimitGuard]',
  standalone: true,
})
export class ImageUploadLimitGuardDirective implements OnInit, OnDestroy {
  @Input('imageUploadLimitGuard') remainingSlots = Number.POSITIVE_INFINITY;
  @Output() imageUploadLimitExceeded = new EventEmitter<ImageUploadLimitExceededEvent>();

  private removeListeners: Array<() => void> = [];

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      const changeListener = (event: Event) => this.handleChange(event);
      const dropListener = (event: Event) => this.handleDrop(event as DragEvent);

      host.addEventListener('change', changeListener, true);
      host.addEventListener('drop', dropListener, true);

      this.removeListeners = [
        () => host.removeEventListener('change', changeListener, true),
        () => host.removeEventListener('drop', dropListener, true),
      ];
    });
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach(removeListener => removeListener());
    this.removeListeners = [];
  }

  private handleChange(event: Event): void {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    const selectedCount = input?.files?.length ?? 0;

    if (this.selectionExceedsRemainingSlots(selectedCount)) {
      const shouldBlockUpload = this.reportExceededSelection(event, selectedCount);
      if (shouldBlockUpload && input) {
        input.value = '';
      }
    }
  }

  private handleDrop(event: DragEvent): void {
    const selectedCount = event.dataTransfer?.files?.length ?? 0;

    if (this.selectionExceedsRemainingSlots(selectedCount)) {
      this.reportExceededSelection(event, selectedCount);
    }
  }

  private selectionExceedsRemainingSlots(selectedCount: number): boolean {
    return selectedCount > this.normalizedRemainingSlots();
  }

  private normalizedRemainingSlots(): number {
    return Number.isFinite(this.remainingSlots) ? Math.max(0, this.remainingSlots) : Number.POSITIVE_INFINITY;
  }

  private reportExceededSelection(event: Event, selectedCount: number): boolean {
    const remainingSlots = this.normalizedRemainingSlots();
    const shouldBlockUpload = remainingSlots <= 0;

    if (shouldBlockUpload) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    this.ngZone.run(() => {
      this.imageUploadLimitExceeded.emit({
        remainingSlots,
        selectedCount,
      });
    });

    return shouldBlockUpload;
  }
}
